import * as types from '../../mutation-types';
import memomapsDataHelper from './memomaps-data-helper';
import repetitionHelper from '../module-app/repetition-helper';
import store from '@/store';

import { updateCourseProgress, fetchCourseProgress } from './userprogress-helper';
import moment from 'moment';
import { EventBus } from '@/event-bus';
import getSystems from './data/systems';

export default {
    async loadAndApplyCourseData({ commit, state, dispatch }, { courseId, level }) {
        const course = state.courses[courseId];

        if (course && (course.loadInfo[level] || course.loadInfo[level + 'Loading'])) {
            if (course.loadInfo[level + 'Loading']) {
                return new Promise(resolve => {
                    const unwatch = store.watch(
                        globalState => {
                            return globalState.moduleCourse.courses[courseId].loadInfo[level];
                        },
                        val => {
                            if (unwatch) {
                                unwatch();
                            }
                            resolve();
                        }
                    );
                });
            } else {
                return;
            }
        }
        commit(types.SET_COURSE_DATA, { courseId, level: level + 'Loading' });

        const { courseData = [], stepsData = [], stepGroupsData = [], memosData = [], scenariosData = [] } = await {
            toc: memomapsDataHelper.loadCourseToc,
            content: memomapsDataHelper.loadCourseFull,
            scenarios: memomapsDataHelper.loadCourseScenarios
        }[level](courseId);
        stepGroupsData.forEach(stepGroupData => {
            commit(types.SET_STEP_GROUP_DATA, {
                stepGroupId: stepGroupData.id,
                data: stepGroupData
            });
        });

        commit(types.SET_STEP_DATA, stepsData);

        commit(types.SET_MEMO_DATA, memosData);

        if (!state.courses[courseId].loadInfo.scenarios) {
            commit(types.SET_SCENARIO_DATA, scenariosData);
        }

        commit(types.SET_COURSE_DATA, { courseId, data: courseData, level });
        if (courseData && courseData.lifeskillNumber) {
            commit(
                'moduleApp/' + types.UPDATE_LIFESKILL_DATA,
                {
                    lifeskillId: courseData.lifeskillNumber,
                    data: { masterCourseId: courseData.id }
                },
                { root: true }
            );
        }
    },

    async loadCourseProgress({ commit, state }) {
        const activitiesNeedingRecalculation = new Set();
        const stepsNeedingRecalculation = new Set();
        const progress = await fetchCourseProgress(state.progressMapping);
        progress.forEach(p => {
            const timestamp = moment(p.createdAt).format('x');

            if (p.context === 'memo') {
                const memo = state.memos[p.contextId];
                if (memo) {
                    activitiesNeedingRecalculation.add(memo.stepId);
                    stepsNeedingRecalculation.add(memo.stepId);

                    if (p.activity === 'summary') {
                        commit(types.SET_MEMO_SUMMARY_PROGRESS, {
                            memoId: p.contextId,
                            timestamp
                        });
                    } else if (p.activity === 'gameA' || p.activity === 'gameB') {
                        commit(types.SET_MEMO_GAME_PROGRESS, {
                            gameId: p.activity,
                            memoId: p.contextId,
                            timestamp
                        });
                    }
                }
            }

            if (p.context === 'step') {
                const stepId = p.contextId;
                if (p.activity === 'read') {
                    stepsNeedingRecalculation.add(stepId);
                    commit(types.SET_STEP_READ_PROGRESS, {
                        stepId,
                        timestamp
                    });
                }
                if (p.activity === 'quiz' || p.activity === 'quiz2') {
                    stepsNeedingRecalculation.add(stepId);
                    commit(types.SET_STEP_QUIZ_PROGRESS, {
                        stepId,
                        activityName: p.activity,
                        timestamp
                    });
                }
            }

            if (p.context === 'stepgroup') {
                commit(types.SET_STEP_GROUP_QUIZ_PROGRESS, {
                    stepGroupId: p.contextId,
                    timestamp: timestamp
                });
            }

            if (p.context === 'scenario') {
                commit(types.UPDATE_STEP_SCENARIO, {
                    scenarioId: p.contextId,
                    done: true,
                    timestamp: timestamp
                });
            }
        });

        activitiesNeedingRecalculation.forEach(stepId => {
            commit(types.RECOMPUTE_STEP_MEMO_PROGRESS, { stepId });
        });
        stepsNeedingRecalculation.forEach(stepId => {
            commit(types.RECOMPUTE_STEP_PROGRESS, { stepId });
        });
    },

    async loadCoursesList({ commit, state }, payload) {
        if (state.courseIds.length) {
            return; // Course list already loaded
        }
        let { courses, mapping } = await memomapsDataHelper.loadCoursesList();
        commit(
            types.SET_COURSE_LIST,
            courses.map(course => course.courseData.id)
        );
        commit('SET_PROGRESS_MAPPING', mapping);

        courses.forEach(({ courseData, stepsData, stepGroupsData, memosData = [], scenariosData = [] }) => {
            stepGroupsData.forEach(stepGroupData => {
                commit(types.SET_STEP_GROUP_DATA, {
                    stepGroupId: stepGroupData.id,
                    data: stepGroupData
                });
            });

            commit(types.SET_STEP_DATA, stepsData);

            commit(types.SET_MEMO_DATA, memosData);

            commit(types.SET_SCENARIO_DATA, scenariosData);

            commit(types.SET_COURSE_DATA, {
                courseId: courseData.id,
                data: courseData,
                level: 'list'
            });
            if (courseData && courseData.lifeskillNumber) {
                commit(
                    'moduleApp/' + types.UPDATE_LIFESKILL_DATA,
                    {
                        lifeskillId: courseData.lifeskillNumber,
                        data: { masterCourseId: courseData.id }
                    },
                    { root: true }
                );
            }
        });
    },

    async loadCourseFull({ commit, state, dispatch }, { courseId }) {
        await dispatch('loadAndApplyCourseData', { courseId, level: 'content' });
    },
    async loadCourseScenarios({ commit, state, dispatch }, { courseId }) {
        await dispatch('loadAndApplyCourseData', { courseId, level: 'scenarios' });
    },

    markMemoSummaryAsDone({ commit, state }, { stepId, memoId }) {
        console.debug('markMemoSummaryAsDone', stepId, memoId);
        commit(types.SET_MEMO_SUMMARY_PROGRESS, {
            stepId,
            memoId,
            timestamp: Date.now()
        });
        commit(types.RECOMPUTE_STEP_MEMO_PROGRESS, { stepId });
        commit(types.RECOMPUTE_STEP_PROGRESS, { stepId });

        const courseId = state.steps[stepId].courseId;
        updateCourseProgress(courseId, 'memo', memoId, 'summary', 'done').catch(console.error);

        window.dataLayer.push({ event: 'summary_done', step: stepId });
    },

    markStepReadAsDone({ commit, state, dispatch }, { stepId }) {
        console.debug('markStepReadAsDone', stepId);
        const step = state.steps[stepId];
        if (step.read.progress === 1) {
            return;
        }
        const timestamp = Date.now();
        const links = step.read.links || [];
        const braindates = [];

        [stepId, ...links].forEach(sId => {
            const s = state.steps[sId];

            if (s.read.progress === 1) {
                return;
            }

            const course = state.courses[s.courseId];

            commit(types.SET_STEP_READ_PROGRESS, { stepId: sId, timestamp });
            commit(types.RECOMPUTE_STEP_PROGRESS, { stepId: sId });
            updateCourseProgress(course.id, 'step', sId, 'read', 'done').catch(console.error);

            braindates.push({
                context: 'course',
                lifeskillNumber: s.fromLifeskill || course.lifeskillNumber || '00',
                courseId: course.id,
                stepId: sId,
                activity: 'stepRead'
            });
        });

        window.dataLayer.push({ event: 'read_done', step: stepId });

        // --------------------------------------
        // Giving braindate
        // --------------------------------------

        braindates.forEach((braindate, i) => {
            if (i === 0) {
                braindate.bonusBraindates = [];
            } else {
                braindates[0].bonusBraindates.push(braindate.lifeskillNumber);
                braindate.isBonus = true;
            }
        });
        braindates.forEach(braindate => {
            dispatch('moduleApp/registerBraindate', braindate, { root: true });
        });
    },

    markStepQuizAsDone({ commit, state, dispatch }, { stepId, activityName }) {
        console.debug('markStepQuizAsDone', stepId, activityName);
        const step = state.steps[stepId];
        if (step[activityName].progress === 1) {
            return;
        }
        const timestamp = Date.now();

        const links = step[activityName].links || [];
        const braindates = [];

        [stepId, ...links].forEach(sId => {
            const s = state.steps[sId];

            if (s[activityName].progress === 1) {
                return;
            }

            const course = state.courses[s.courseId];

            commit(types.SET_STEP_QUIZ_PROGRESS, {
                stepId: sId,
                activityName,
                timestamp
            });
            commit(types.RECOMPUTE_STEP_PROGRESS, { stepId: sId });

            updateCourseProgress(s.courseId, 'step', sId, activityName, 'done').catch(console.error);

            braindates.push({
                context: 'course',
                lifeskillNumber: s.fromLifeskill || course.lifeskillNumber || '00',
                courseId: course.id,
                stepId,
                activity: 'stepQuiz'
            });
        });

        window.dataLayer.push({ event: 'quiz_done', step: stepId });

        // --------------------------------------
        // Giving braindate
        // --------------------------------------

        braindates.forEach((braindate, i) => {
            if (i === 0) {
                braindate.bonusBraindates = [];
            } else {
                braindates[0].bonusBraindates.push(braindate.lifeskillNumber);
                braindate.isBonus = true;
            }
        });
        braindates.forEach(braindate => {
            dispatch('moduleApp/registerBraindate', braindate, { root: true });
        });
    },

    markMemoGameAsDone({ commit, state, dispatch }, { stepId, activityName, memoId = null, gameId }) {
        console.debug('markMemoGameAsDone', stepId, activityName, memoId, gameId);
        const step = state.steps[stepId];
        let memoIds = [];
        if (memoId) {
            memoIds = [memoId];
        } else if (activityName) {
            memoIds = step[activityName].memoIds;
        }
        const courseId = step.courseId;

        const memos = memoIds.map(memoId => state.memos[memoId]);
        const gameAlreadyCompleted = memos.every(memo => memo[gameId] && memo[gameId].done);

        memoIds.forEach(memoId => {
            commit(types.SET_MEMO_GAME_PROGRESS, {
                stepId,
                activityName,
                gameId,
                memoId,
                timestamp: Date.now()
            });
            updateCourseProgress(courseId, 'memo', memoId, gameId, 'done').catch(console.error);
        });

        commit(types.RECOMPUTE_STEP_MEMO_PROGRESS, { stepId });
        commit(types.RECOMPUTE_STEP_PROGRESS, { stepId });

        window.dataLayer.push({ event: 'game_done', step: stepId, game: gameId });

        // --------------------------------------
        // Giving braindate
        // --------------------------------------

        if (activityName && gameId === 'gameB' && !gameAlreadyCompleted) {
            const course = state.courses[courseId];
            dispatch(
                'moduleApp/registerBraindate',
                {
                    context: 'course',
                    lifeskillNumber: step.fromLifeskill || course.lifeskillNumber || '00',
                    courseId,
                    stepId,
                    activityName,
                    memoId,
                    activity: 'stepMemo',
                    game: 'gameB'
                },
                { root: true }
            );
        }
    },

    markStepScenarioAsDone({ commit, state, getters, rootGetters, dispatch }, { scenarioId, stepId, scenarioIndex }) {
        const scenario = state.scenarios[scenarioId];

        if (scenario && scenario.done) {
            return;
        }
        commit(types.UPDATE_STEP_SCENARIO, {
            scenarioId,
            done: true,
            timestamp: Date.now()
        });

        updateCourseProgress('', 'scenario', scenarioId, 'scenario', 'done').catch(console.error);

        window.dataLayer.push({
            event: 'scenario_done',
            step: stepId,
            scenario: scenarioIndex
        });

        // --------------------------------------
        // Awarding life key for habit-journey
        // --------------------------------------

        const step = state.steps[stepId];
        const journeyStats = getters['getStepGroupStats'](step.stepGroupId);
        const context = `habit-journey:${step.stepGroupId}`;
        const courseId = step.courseId;
        const course = state.courses[courseId];
        const lifeskill = step.fromLifeskill || course.lifeskillNumber;
        const alreadyAwarded = rootGetters['moduleApp/lifekeys'].some(
            key => key.lifeskill === lifeskill && key.context === context
        );

        if (!alreadyAwarded && journeyStats.scenariosDoneCount === journeyStats.scenariosTotalCount) {
            dispatch('moduleApp/awardLifekey', { lifeskill, context }, { root: true });
        }

        // --------------------------------------
        // Giving braindate
        // --------------------------------------

        dispatch(
            'moduleApp/registerBraindate',
            {
                context: 'course',
                lifeskillNumber: step.fromLifeskill || course.lifeskillNumber || '00',
                courseId: course.id,
                stepId,
                scenarioIndex,
                activity: 'scenario'
            },
            { root: true }
        );
    },

    updateStepGroupRepetition({ state }, payload) {
        const stepGroup = state.stepGroups[payload.stepGroupId];
        if (stepGroup.repetition) {
            repetitionHelper.updateRepetition(
                stepGroup.repetition.id,
                'memomaps-step-group',
                payload.stepGroupId,
                payload.name
            );
        } else {
            repetitionHelper.createRepetition(
                'memomaps-step-group',
                payload.stepGroupId,
                stepGroup.name,
                null,
                null,
                `${location.origin}/from-rep-email`
            );
        }
        window.dataLayer.push({
            event: 'repetition_done',
            step_group_id: payload.stepGroupId,
            step_group_name: payload.name
        });
    },

    resetStepGroupQuizProgress({ commit }, { stepGroupId }) {
        commit(types.UPDATE_STEP_GROUP_QUIZ_STATE, {
            stepGroupId,
            reset: true
        });
    },

    registerStepGroupQuizResult({ commit, getters, dispatch }, { stepGroupId, summary }) {
        const previousQuizState = getters.getStepGroupQuizState(stepGroupId) || {
            totalCount: summary.totalCount,
            correctCount: 0,
            allCompletedItems: []
        };

        const accumulatedCorrectCount = previousQuizState.correctCount + summary.correctCount;
        const allCompletedItems = [...previousQuizState.allCompletedItems, ...summary.completedItems];

        commit(types.UPDATE_STEP_GROUP_QUIZ_STATE, {
            stepGroupId,
            totalCount: previousQuizState.totalCount,
            correctCount: accumulatedCorrectCount,
            allCompletedItems,
            progress: accumulatedCorrectCount / previousQuizState.totalCount,
            failedItems: summary.failedItems
        });

        if (summary.success) {
            dispatch('markStepGroupQuizAsDone', { stepGroupId });
            dispatch('updateStepGroupRepetition', { stepGroupId });
        }
    },

    markStepGroupQuizAsDone({ commit, state, rootGetters, dispatch }, { stepGroupId }) {
        console.debug('markStepGroupQuizAsDone', stepGroupId);
        const stepGroup = state.stepGroups[stepGroupId];
        if (stepGroup.quiz.done) {
            return;
        }

        commit(types.SET_STEP_GROUP_QUIZ_PROGRESS, {
            stepGroupId,
            timestamp: Date.now()
        });
        const courseId = stepGroup.courseId;

        updateCourseProgress(courseId, 'stepgroup', stepGroupId, 'quiz', 'done').catch(console.error);

        const course = state.courses[courseId];
        const lifeskillId = course.lifeskillNumber || (courseId === 'top-10' ? '41' : undefined); // NOTE: quick hack for top-10

        EventBus.$emit('journey-completed', lifeskillId);
        const lifeskillStats = rootGetters['moduleApp/getLifeskillStats'](lifeskillId);
        if (lifeskillStats.numberOfScenarios > 0 && lifeskillStats.acquiredTheoryLifekeys === 1) {
            EventBus.$emit('scenarios-unlocked', lifeskillId);
        }
        window.dataLayer.push({
            event: 'step_group_quiz_done',
            step_group_id: stepGroupId
        });

        // --------------------------------------
        // Awarding life key for theory-journey
        // --------------------------------------

        const context = `theory-journey:${stepGroup.id}`;
        const alreadyAwarded = rootGetters['moduleApp/lifekeys'].some(
            key => key.lifeskill === lifeskillId && key.context === context
        );

        if (!alreadyAwarded) {
            dispatch('moduleApp/awardLifekey', { lifeskill: lifeskillId, context }, { root: true });
        }

        // --------------------------------------
        // Giving braindate
        // --------------------------------------

        dispatch(
            'moduleApp/registerBraindate',
            {
                context: 'course',
                lifeskillNumber: lifeskillId || '00',
                courseId: course.id,
                stepGroupId,
                activity: 'stepGroupQuiz'
            },
            { root: true }
        );
    },

    dbgStepMarkAll({ commit, state, dispatch }, { stepId, reset }) {
        const step = state.steps[stepId];
        step.activityNames.forEach(activityName => {
            dispatch('dbgMarkActivity', { stepId, activityName, reset });
        });
    },
    dbgMarkActivity({ commit, state, dispatch }, { stepId, activityName, reset }) {
        // NOTE: ignore if reset
        if (reset) {
            return;
        }
        if (activityName === 'read') {
            dispatch('markStepReadAsDone', { stepId, reset });
        } else if (activityName === 'memorize' || activityName === 'memorize2') {
            const step = state.steps[stepId];
            step[activityName].memoIds.forEach(memoId => {
                dispatch('markMemoSummaryAsDone', { stepId, memoId, reset });
            });
            dispatch('markMemoGameAsDone', {
                stepId,
                activityName,
                gameId: 'gameA',
                reset
            });
            dispatch('markMemoGameAsDone', {
                stepId,
                activityName,
                gameId: 'gameB',
                reset
            });
        } else if (activityName === 'quiz' || activityName === 'quiz2') {
            dispatch('markStepQuizAsDone', { stepId, activityName, reset });
        }
    },

    async loadSystems({ commit }) {
        const systems = await getSystems();

        commit(types.SET_SYSTEMS, systems);
    }
};
